import type { ResumeCardResponse } from "./apiContracts";
import type { PublicIndexResponse } from "./apiTopicContracts";
import type { BillingCycle, PlanCode } from "./apiContracts";
import type { ClientPlanContext } from './clientPlanContext';

export type ViewerEnvelope = {
  viewer: {
    authStatus: "visitor" | "authenticated";
    plan: PlanCode;
    userId: string | null;
    email: string | null;
    displayName?: string;
    sessionHash?: string | null;
		planContext?: ClientPlanContext;
  };
	capabilities?: {
		crossCycleUpgrade?: boolean;
		topicTrendingV1?: boolean;
		topicNewestV1?: boolean;
		audioBackgroundHandoff?: boolean;
		communitySubmissionsEnabled?: boolean;
		communityCommentsEnabled?: boolean;
	};
  [k: string]: unknown;
};

type PaidPlan = Exclude<PlanCode, 'visitor' | 'free'>;

export type SameCycleUpgradePreview = {
	behavior: 'same_cycle_proration';
	subscriptionId: string;
	fromPlan: PaidPlan;
	toPlan: PaidPlan;
	billingCycle: BillingCycle;
	todayDueCents: number;
	immediateQuotaDelta: number;
	targetPlanQuota: number;
	accessUntil: string | null;
	quotedAt: string;
};

export type CrossCycleUpgradePreview = {
	behavior: 'cross_cycle_refund_and_rebill';
	subscriptionId: string;
	fromPlan: PaidPlan;
	toPlan: PaidPlan;
	fromBillingCycle: BillingCycle;
	billingCycle: BillingCycle;
	targetCycleChargeCents: number;
	currency: string;
	refundAmountKnown: false;
	currentPlanQuota: number;
	targetPlanQuota: number;
	accessUntil: string | null;
	quotedAt: string;
};

export type BillingUpgradePreviewResponse = SameCycleUpgradePreview | CrossCycleUpgradePreview;

export function parseBillingUpgradePreviewResponse(value: unknown): BillingUpgradePreviewResponse {
	const input = record(value);
	if (!input) throw invalidUpgradePreview();
	const shared = parseUpgradePreviewShared(input);
	if (input.behavior === 'same_cycle_proration') {
		return {
			behavior: input.behavior,
			...shared,
			todayDueCents: nonnegativeInteger(input.todayDueCents),
			immediateQuotaDelta: nonnegativeInteger(input.immediateQuotaDelta),
			targetPlanQuota: nonnegativeInteger(input.targetPlanQuota),
		};
	}
	if (input.behavior === 'cross_cycle_refund_and_rebill') {
		if (
			input.refundAmountKnown !== false
			|| !isBillingCycle(input.fromBillingCycle)
			|| typeof input.currency !== 'string'
			|| !input.currency.trim()
		) throw invalidUpgradePreview();
		return {
			behavior: input.behavior,
			...shared,
			fromBillingCycle: input.fromBillingCycle,
			targetCycleChargeCents: nonnegativeInteger(input.targetCycleChargeCents),
			currency: input.currency,
			refundAmountKnown: false,
			currentPlanQuota: nonnegativeInteger(input.currentPlanQuota),
			targetPlanQuota: nonnegativeInteger(input.targetPlanQuota),
		};
	}
	throw invalidUpgradePreview();
}

function parseUpgradePreviewShared(input: Record<string, unknown>) {
	if (
		typeof input.subscriptionId !== 'string'
		|| !input.subscriptionId.trim()
		|| !isPaidPlan(input.fromPlan)
		|| !isPaidPlan(input.toPlan)
		|| !isBillingCycle(input.billingCycle)
	) throw invalidUpgradePreview();
	if (input.accessUntil !== null && !isIsoTimestamp(input.accessUntil)) throw invalidUpgradePreview();
	if (!isIsoTimestamp(input.quotedAt)) throw invalidUpgradePreview();
	return {
		subscriptionId: input.subscriptionId,
		fromPlan: input.fromPlan,
		toPlan: input.toPlan,
		billingCycle: input.billingCycle,
		accessUntil: input.accessUntil,
		quotedAt: input.quotedAt,
	};
}

function record(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function isPaidPlan(value: unknown): value is PaidPlan {
	return value === 'lite' || value === 'pro' || value === 'max';
}

function isBillingCycle(value: unknown): value is BillingCycle {
	return value === 'monthly' || value === 'quarterly' || value === 'annual';
}

function nonnegativeNumber(value: unknown): number {
	if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw invalidUpgradePreview();
	return value;
}

function nonnegativeInteger(value: unknown): number {
	const parsed = nonnegativeNumber(value);
	if (!Number.isSafeInteger(parsed)) throw invalidUpgradePreview();
	return parsed;
}

function isIsoTimestamp(value: unknown): value is string {
	if (typeof value !== 'string') return false;
	const timestamp = Date.parse(value);
	return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

function invalidUpgradePreview(): Error {
	return new Error('invalid_billing_upgrade_preview');
}

export type TopicListResponse = {
  topics: PublicIndexResponse["topics"];
  totalTopics: number;
  totalMinutes: number;
  newThisWeek: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextCursor: string | null;
};

export type CommunityIdeaDetailResponse = {
  idea: {
    id: string;
    title: string;
    subtitle: string | null;
    isAdopted: boolean;
    displayStatus: 'Voting' | 'Selected';
    viewerLiked: boolean;
    strengthenItems: string[];
    weakenItems: string[];
    links: Array<{ url: string; label?: string }>;
    attachments: Array<{ name: string; size: number; mimeType: string }>;
    publicIndexable: boolean;
		available?: boolean;
    navigableTopicSlug: string | null;
  } | null;
  source: string;
};

export type CommunityIdeaListResponse = {
	ideas: Array<{
		id: string; title: string; subtitle?: string | null; description: string | null; status: string; isAdopted?: boolean; viewerLiked?: boolean;
		commentsCount: number; linksCount: number; attachmentsCount: number; category: string | null;
		navigableTopicSlug?: string | null; createdAt: string;
		topicTitle?: string | null; topicSubtitle?: string | null; topicSummary?: string | null; topicCreatedAt?: string | null;
		topicChapterCount?: number | null; topicAudioMinutes?: number | null; topicClassification?: import('../../domain/topicClassification').TopicClassification | null; topicCoverImageUrl?: string | null;
	}>;
	hasMore: boolean;
	nextCursor: string | null;
	windowEnd?: string;
	historicalWindow?: boolean;
	viewerVoteAllowance?: { limit: number; remaining: number; resetAt: string };
};

export type AiJobDetailResponse = {
  job: {
    id: string;
    title: string;
    subtitle: string | null;
    description: string | null;
    prompt: string;
    requestedChapters: string;
    requestedChaptersRange?: string | null;
    status: string;
    queuedAt: string;
    completedAt: string | null;
    strengthenItems: string[];
    weakenItems: string[];
    links: Array<{ url: string; label: string }>;
    attachments: Array<{ name: string; size?: number; mimeType?: string }>;
    topicSlug: string | null;
    chapterCount: number | null;
  } | null;
  source: string;
};

export type SiteMetricsResponse = {
  activeLearners: number;
  structuredTopics: number;
};

export type AccountDashboardResponse = {
	user: { id: string; email: string; displayName: string; memberSince: string; hasPassword: boolean };
	subscription: { plan: string; status: string; currentPeriodStart: string | null; currentPeriodEnd: string | null };
	planContext: {
		billingPlan: 'free' | 'lite' | 'pro' | 'max';
		promotionalAccessPlan: 'lite' | 'pro' | 'max' | null;
		effectiveAccessPlan: 'free' | 'lite' | 'pro' | 'max';
		temporaryAccess: {
			tiers: Array<{ plan: 'lite' | 'pro' | 'max'; endsAt: string }>;
			currentTierEndsAt: string;
			finalEndsAt: string;
		} | null;
	};
	learning: { topicsExplored: number; progressTotal: number; favoritesCount: number; chaptersCompleted: number; aiCoursesCreated: number };
	history: Array<{
		topicId: string;
		topicSlug: string | null;
		chapterId: string;
		progressPercent: number;
		updatedAt: string;
		topicTitle: string;
		chapterIndex: number | null;
		chapterTitle: string | null;
		coverImageUrl: string | null;
	}>;
};

export type LearningStatsResponse = {
	topicsExplored: number;
	lessonsCompleted: number;
	articlesRead: number;
	audioListened: number;
	videosWatched: number;
	daysActive: number;
};

export type QuotaUsageResponse = {
	summary: {
		quotaKind: string;
		used: number;
		limit: number;
		remaining: number;
		billingMonth: string;
		resetDate: string | null;
		grantPending: boolean;
		planBase: number;
		consumed: number;
		reserved: number;
		available: number;
	};
	entries: Array<{
		id: string;
		quotaKind: string;
		amount: number;
		sourceType: string;
		sourceId: string | null;
		billingMonth: string;
		reason: string | null;
		createdAt: string;
	}>;
	hasMore: boolean;
	nextCursor: string | null;
};

export type PromotionHistoryResponse = {
	items: Array<{
		displayName: string;
		rewardType: 'access_plan' | 'ai_credit';
		rewardPlan: 'lite' | 'pro' | 'max' | null;
		rewardAmount: number | null;
		startsAt: string;
		endsAt: string;
		revokedAt: string | null;
		status: 'active' | 'expired' | 'revoked';
	}>;
	nextCursor: string | null;
	planContext: AccountDashboardResponse['planContext'];
};

export type CategoryInterestsResponse = {
	categories: Array<{
		id: string;
		slug: string;
		label: string;
		engagementScore: number;
	}>;
};

export type ResumeCardFetcher = (path: string) => Promise<ResumeCardResponse>;
